/* eslint-disable */

import { consoleLogger } from '@shared/logging/consoleLogger';

type PredicateOutput<Res> =
  | { kind: 'skip'; msg?: string; rest: unknown[] }
  | { kind: 'pass'; msg: string | undefined; rest: unknown[]; res: Res };

type Loggable =
  | (PredicateOutput<any> & { isTerminal: boolean })
  | { kind: 'eval'; snap: any }
  | { kind: 'halt'; msg: string; rest: any[] }
  | { kind: 'log'; args: any[] }
  | { kind: 'err'; err: Error };

type Predicate<Ctx, Snap, ParentRes, SelfRes> = (
  res: ParentRes,
  input: {
    ctx: Ctx;
    snap: Snap;
    name: string;
    log: (...args: any[]) => void;
    cond: <PassRes>(
      res: PassRes,
      msg: string,
      ...rest: any[]
    ) => PredicateOutput<
      Exclude<PassRes, false | null | undefined | 0 | void | ''>
    >;
    skip: (
      msg?: string,
      ...rest: any[]
    ) => { kind: 'skip'; msg?: string; rest: unknown[] };
    pass: <PassRes>(
      res: PassRes,
      msg?: string,
      ...rest: any[]
    ) => {
      kind: 'pass';
      msg: string | undefined;
      rest: unknown[];
      res: PassRes;
    };
  },
) => PredicateOutput<SelfRes>;

type Action<Ctx, Snap, Res> = (
  res: Res,
  input: {
    ctx: Ctx;
    snap: Snap;
    name: string;
    run: () => void;
    wait: () => void;
    halt: (msg: any, ...rest: any[]) => void;
    log: (...args: any[]) => void;
  },
) => void;

export type Block<Ctx, Snap, ParentRes, SelfRes> = {
  name: string;
  predicate: Predicate<Ctx, Snap, ParentRes, SelfRes>;
  then: Action<Ctx, Snap, SelfRes> | Array<Block<Ctx, Snap, SelfRes, any>>;
};

export type Maker<Ctx, Snap, ParentRes> = (args: {
  makeEffect: <SelfRes>(
    name: string,
    predicate: Predicate<Ctx, Snap, ParentRes, SelfRes>,
    action: Action<Ctx, Snap, SelfRes>,
  ) => Block<Ctx, Snap, ParentRes, SelfRes>;

  makeEffectGroup: <SelfRes>(
    name: string,
    predicate: Predicate<Ctx, Snap, ParentRes, SelfRes>,
    maker: Maker<Ctx, Snap, SelfRes>,
  ) => Block<Ctx, Snap, ParentRes, SelfRes>;
}) => Array<Block<Ctx, Snap, ParentRes, any>>;

export function defineMachine<Ctx = {}, Snap = {}>(
  name: string,
  makeCtx: () => Ctx,
): (
  maker: Maker<Ctx, Snap, null>,
) => [ctx: Ctx, blocks: Block<Ctx, Snap, null, null>, () => void] {
  let running = true;

  function bindMakerFunctions<ParentRes>(): Parameters<
    Maker<Ctx, Snap, ParentRes>
  >[0] {
    function makeEffect<SelfRes>(
      name: string,
      predicate: Predicate<Ctx, Snap, ParentRes, SelfRes>,
      then: Action<Ctx, Snap, SelfRes>,
    ): Block<Ctx, Snap, ParentRes, SelfRes> {
      return { name, predicate, then };
    }

    function makeEffectGroup<SelfRes>(
      name: string,
      predicate: Predicate<Ctx, Snap, ParentRes, SelfRes>,
      maker: Maker<Ctx, Snap, SelfRes>,
    ): Block<Ctx, Snap, ParentRes, SelfRes> {
      return { name, predicate, then: maker(bindMakerFunctions()) };
    }

    return { makeEffectGroup, makeEffect };
  }

  return (maker: Maker<Ctx, Snap, null>) => {
    const block: Block<Ctx, Snap, null, null> = {
      name,
      predicate: () =>
        running
          ? { kind: 'pass', msg: 'running', rest: [], res: null }
          : { kind: 'skip', msg: 'halted', rest: [] },
      then: maker(bindMakerFunctions()),
    };
    return [
      makeCtx(),
      block,
      () => {
        running = false;
      },
    ];
  };
}

function doLog(
  verbosity: 0 | 1 | 2 | 3 | 4 | 5,
  names: string[],
  loggable: Loggable,
) {
  const consoleLog =
    /*process.env.NODE_ENV === 'test'
      ? (...args: any) => {
          process.stdout.write(
            args
              .map((x: any) => {
                return JSON.stringify(x);
              })
              .join(' ') + '\n',
          );
        }
      :*/ console.log;

  if (loggable.kind === 'err') {
    const log = consoleLog.bind(null, ' err', names.join('::'));
    switch (verbosity) {
      case 5:
      case 4:
      case 3:
      case 2:
      case 1:
      case 0:
        return log(loggable.err);
    }
  }

  if (loggable.kind === 'log') {
    const log = consoleLog.bind(null, ' log', names.join('::'));
    switch (verbosity) {
      case 5:
      case 4:
      case 3:
      case 2:
      case 1:
        return log(...loggable.args);
      case 0:
        return;
    }
  }

  if (loggable.kind === 'eval') {
    const log = consoleLog.bind(null, 'eval', names.join('::'));
    switch (verbosity) {
      case 5:
      case 4:
      case 3:
        return log(loggable.snap);
      case 2:
      case 1:
      case 0:
        return;
    }
  }

  if (loggable.kind === 'halt') {
    const log = consoleLog.bind(null, 'halt', names.join('::'));
    switch (verbosity) {
      case 5:
      case 4:
      case 3:
      case 2:
      case 1:
        return log(loggable.msg, ...loggable.rest);
      case 0:
        return;
    }
  }

  if (loggable.kind === 'skip') {
    const log = consoleLog.bind(null, 'skip', names.join('::'));
    if (loggable.isTerminal)
      switch (verbosity) {
        case 5:
        case 4:
        case 3:
          return log(loggable.msg, ...loggable.rest);
        case 2:
          if (loggable.msg) return log(loggable.msg);
        case 1:
        case 0:
          return;
      }
    else
      switch (verbosity) {
        case 5:
          return log(loggable.msg, ...loggable.rest);
        case 4:
          return log(loggable.msg);
        case 3:
        case 2:
        case 1:
        case 0:
          return;
      }
  }

  if (loggable.kind === 'pass') {
    const log = consoleLog.bind(null, 'pass', names.join('::'));
    if (loggable.isTerminal)
      switch (verbosity) {
        case 5:
        case 4:
        case 3:
          return log(loggable.msg, loggable.res, ...loggable.rest);
        case 2:
          return log(loggable.msg, loggable.res);
        case 1:
          return log(loggable.res);
        case 0:
          return;
      }
    else
      switch (verbosity) {
        case 5:
          return log(loggable.msg, loggable.res, ...loggable.rest);
        case 4:
          return log(loggable.msg, loggable.res);
        case 3:
          return log(loggable.res);
        case 2:
        case 1:
        case 0:
          return;
      }
  }

  throw new Error(`invalid loggable (${JSON.stringify(loggable)}`);
}

export function runMachine<Ctx, Snap>(
  [ctx, block, stopRunning]: [Ctx, Block<Ctx, Snap, null, null>, () => void],
  makeSnapshot: (ctx: Ctx) => Snap,
) {
  function run(_verbosity = 0 as 0 | 1 | 2 | 3 | 4 | 5): Promise<void> {
    const verbosity = (
      {
        silent: 0,
        error: 0,
        warn: 0,
        info: 1,
        verbose: 2,
      } as const
    )[consoleLogger.logLevel];

    const log = doLog.bind(null, verbosity);
    const snap = makeSnapshot(ctx);

    function findAction<ParentRes, SelfRes>(
      namesAcc: Array<string>,
      parentRes: ParentRes,
      block: Block<Ctx, Snap, ParentRes, SelfRes>,
    ): {
      res: SelfRes;
      action: Action<Ctx, Snap, SelfRes>;
      names: string[];
    } | null {
      const names = [...namesAcc, block.name];
      const predicateOutput = block.predicate(parentRes, {
        ctx,
        snap,
        name: names.join('::'),
        log: (...args: any[]) => {
          log(names, { kind: 'log', args });
        },
        cond: <PassRes,>(res: PassRes, msg: string, ...rest: any[]) =>
          res
            ? { kind: 'pass', msg, rest, res: res as any }
            : { kind: 'skip', msg, rest },
        skip: (msg?: string, ...rest: any[]) => ({ kind: 'skip', msg, rest }),
        pass: <PassRes,>(res: PassRes, msg?: string, ...rest: any[]) => ({
          kind: 'pass',
          msg,
          rest,
          res,
        }),
      });

      log(names, {
        ...predicateOutput,
        isTerminal: typeof block.then === 'function',
      });

      if (predicateOutput.kind === 'skip') return null;

      let { res } = predicateOutput;

      if (typeof block.then === 'function')
        return {
          res: res as any,
          action: block.then,
          names,
        };

      for (const group of block.then) {
        const groupResult = findAction<typeof res, any>(
          names,
          res,
          group as any,
        );

        if (groupResult !== null) return groupResult;
      }

      return null;
    }

    log([block.name], { kind: 'eval', snap });
    const foundAction = findAction([], null, block);

    if (!foundAction) return Promise.resolve();

    const { action, res, names } = foundAction;

    let shouldRunAgain = true;
    try {
      action(res, {
        ctx,
        snap,
        name: names.join('::'),
        log: (...args: any[]) => log(names, { kind: 'log', args }),
        run: () => setTimeout(run, 1, verbosity),
        wait: () => {
          shouldRunAgain = false;
        },
        halt: function (msg: any, ...rest: any[]): void {
          stopRunning();
          log(names, { kind: 'halt', msg, rest });
        },
      });
    } catch (err) {
      consoleLogger.logError(names.join('::'), err);
    }

    if (shouldRunAgain) {
      return new Promise((done) =>
        setTimeout((...args) => run(...args).then(() => done()), 1, verbosity),
      );
    } else {
      return Promise.resolve();
    }
  }

  return run;
}
